import { Psd } from 'ag-psd';
import { mapPSDToJSON, uploadImagesToS3 } from './layerMapper';
import { PSDJSON } from './types';
import { hashBuffer, getCachedImageUrl, saveImageUrlToCache } from './cacheManager';
import { parsePSDWithErrorHandling, validatePSD } from './parsePsdHelpers';

export async function extractThumbnail(psd: Psd, fileName?: string): Promise<string | null> {
  try {
    if (!psd.canvas) {
      console.warn('PSD does not have a canvas/preview image');
      return null;
    }

    // Convert canvas to blob/data URL (browser-compatible)
    return new Promise<string | null>((resolve) => {
      const extractAndUpload = async (buffer: Uint8Array) => {
        try {
          // Check cache first
          const hash = await hashBuffer(buffer);
          const cachedUrl = getCachedImageUrl(hash);
          if (cachedUrl) {
            console.log(`✅ Thumbnail cache hit (hash: ${hash.substring(0, 8)}...)`);
            resolve(cachedUrl);
            return;
          }
          
          // Generate unique filename for thumbnail
          // Use hash prefix to ensure uniqueness while keeping it readable
          const hashPrefix = hash.substring(0, 12);
          const baseName = fileName 
            ? fileName.replace(/\.psd$/i, '').replaceAll(/[^a-zA-Z0-9]/g, '_').substring(0, 30)
            : 'thumbnail';
          const uniqueFilename = `${baseName}_${hashPrefix}_thumb.png`;
          
          console.log(`📸 Uploading thumbnail: ${uniqueFilename} (${(buffer.length / 1024).toFixed(2)} KB)`);
          
          // Upload thumbnail to S3
          const s3UrlMap = await uploadImagesToS3([{
            layerId: 'thumbnail',
            buffer: buffer,
            filename: uniqueFilename
          }]);
          
          const url = s3UrlMap.get('thumbnail') || null;
          if (url) {
            console.log(`✅ Thumbnail uploaded successfully: ${url}`);
            if (hash) {
              saveImageUrlToCache(hash, url);
            }
          } else {
            console.warn('⚠️ Thumbnail upload returned no URL');
          }
          resolve(url);
        } catch (error) {
          console.warn('Error extracting thumbnail:', error);
          resolve(null);
        }
      };
      
      if (typeof (psd.canvas as any).toBlob === 'function') {
        (psd.canvas as any).toBlob(async (blob: Blob | null) => {
          if (blob) {
            const arrayBuffer = await blob.arrayBuffer();
            const buffer = new Uint8Array(arrayBuffer);
            await extractAndUpload(buffer);
          } else {
            resolve(null);
          }
        }, 'image/png');
      } else if (typeof (psd.canvas as any).toDataURL === 'function') {
        try {
          const dataURL = (psd.canvas as any).toDataURL('image/png');
          const base64 = dataURL.split(',')[1];
          if (!base64) {
            console.warn('Failed to extract base64 from data URL');
            resolve(null);
            return;
          }
          const binaryString = atob(base64);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            const codePoint = binaryString.codePointAt(i);
            if (codePoint !== undefined) {
              bytes[i] = codePoint;
            }
          }
          // Properly await the upload
          extractAndUpload(bytes).catch((error) => {
            console.error('Error uploading thumbnail from toDataURL:', error);
            resolve(null);
          });
        } catch (error) {
          console.error('Error extracting thumbnail with toDataURL:', error);
          resolve(null);
        }
      } else {
        console.warn('Canvas does not support toBlob or toDataURL');
        resolve(null);
      }
    });
  } catch (error) {
    console.warn('Error extracting thumbnail:', error);
    return null;
  }
}

export async function parsePSDFile(
  fileBuffer: ArrayBuffer
): Promise<PSDJSON> {
  try {
    const psd = await parsePSDWithErrorHandling(fileBuffer);
    validatePSD(psd);
    
    // Map PSD to JSON structure (images will be uploaded to S3 automatically)
    const json = await mapPSDToJSON(psd);
    
    return json;
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    console.error('Error parsing PSD:', errorMessage);
    throw new Error(`Failed to parse PSD file: ${errorMessage}`);
  }
}

export async function parsePSDFileWithThumbnail(
  fileBuffer: ArrayBuffer,
  fileName?: string
): Promise<{ json: PSDJSON; thumbUrl: string | null }> {
  try {
    const psd = await parsePSDWithErrorHandling(fileBuffer);
    validatePSD(psd);
    
    // Extract thumbnail (uploads to S3) with filename for unique naming
    const thumbUrl = await extractThumbnail(psd, fileName);
    
    // Map PSD to JSON structure (images will be uploaded to S3 automatically)
    const json = await mapPSDToJSON(psd);
    
    return { json, thumbUrl: thumbUrl || '' };
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    console.error('Error parsing PSD:', errorMessage);
    throw new Error(`Failed to parse PSD file: ${errorMessage}`);
  }
}
